import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { EyeIcon, EyeOffIcon, KanbanSquareIcon, ListChecksIcon, SlidersHorizontalIcon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { updateLeadStage } from '../../services/leadService';
import { usePreferences } from '../../hooks/usePreferences';
import { formatDate, fullName } from '../../utils/format';
import { PageHeader } from '../../components/crm/PageHeader';
import { DemoBadge } from '../../components/crm/DemoBadge';
import { EmptyState } from '../../components/crm/EmptyState';
import { SourceBadge } from '../../components/crm/SourceBadge';
import { StageManager } from '../../components/crm/StageManager';
import { noteColor } from '../../components/crm/noteColors';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Switch } from '../../components/ui/Switch';
import { Checkbox } from '../../components/ui/Checkbox';

/** Campos que se pueden mostrar en cada tarjeta del embudo. */
const CARD_FIELDS: {
  key: string;
  label: string;
}[] = [{
  key: 'company',
  label: 'Empresa'
}, {
  key: 'phone',
  label: 'Teléfono'
}, {
  key: 'email',
  label: 'Email'
}, {
  key: 'budget',
  label: 'Presupuesto'
}, {
  key: 'service',
  label: 'Tipo de servicio'
}, {
  key: 'note',
  label: 'Última nota'
}, {
  key: 'owner',
  label: 'Comercial'
}, {
  key: 'date',
  label: 'Fecha'
}, {
  key: 'lastContact',
  label: 'Último contacto'
}];
export function Pipeline() {
  const {
    user,
    members,
    hasPermission
  } = useAuth();
  const {
    organizationId,
    leads,
    stages,
    sources,
    notes,
    isDemo
  } = useWorkspace();
  const [preferences, updatePreferences] = usePreferences();
  const cards = preferences.pipelineCards;
  const [dragging, setDragging] = useState<string | null>(null);
  const [overStage, setOverStage] = useState<string | null>(null);
  const [hidden, setHidden] = useState<string[]>([]);
  const [cardsOpen, setCardsOpen] = useState(false);
  const [stagesOpen, setStagesOpen] = useState(false);
  const canEdit = hasPermission('leads.edit');
  const authorName = user ? fullName(user.firstName, user.lastName) : 'Sistema';
  const compact = cards.mode === 'compacto';
  function setCards(patch: Partial<typeof cards>) {
    updatePreferences({
      pipelineCards: {
        ...cards,
        ...patch
      }
    });
  }
  function shows(field: string) {
    if (compact) return field === 'company' || field === 'phone';
    return cards.fields.includes(field);
  }
  function lastNote(leadId: string) {
    return notes.filter(n => n.leadId === leadId).sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  }
  async function drop(stageId: string) {
    if (dragging) await updateLeadStage(dragging, stageId, authorName);
    setDragging(null);
    setOverStage(null);
  }
  const columns = stages.filter(s => s.active && !hidden.includes(s.id));
  if (leads.length === 0) {
    return <>
        <PageHeader title="Embudo de ventas" description="Tus oportunidades por estado." data-id="element-1180" data-mp="3muu57hd0c6ge" />
        <EmptyState icon={<KanbanSquareIcon className="h-5 w-5" data-id="element-1182" data-mp="3qxqg0lwb2y9s" />} title="Tu embudo está vacío" description="Cuando recibas leads desde tus landings aparecerán aquí para que puedas moverlos entre estados." action={<Link to="/app/landings" data-id="element-1183" data-mp="9nscxztvixj0">
              <Button data-id="element-1184" data-mp="9mr13t51a708">Crear una landing</Button>
            </Link>} data-id="element-1181" data-mp="3qwfcmn6rvsj7" />
      </>;
  }
  return <>
      <PageHeader title="Embudo de ventas" description="Arrastra cada lead a su nuevo estado." actions={<>
            {isDemo && <DemoBadge className="self-center" data-id="element-1186" data-mp="nf6zggmzpgut" />}
            <Button variant="secondary" leftIcon={<SlidersHorizontalIcon className="h-4 w-4" data-id="element-1188" data-mp="sfbf64g2q7jz" />} onClick={() => setCardsOpen(true)} data-id="element-1187" data-mp="nh1hnn6vmuv7">
              Personalizar tarjetas
            </Button>
            <Button variant="secondary" leftIcon={<ListChecksIcon className="h-4 w-4" data-id="element-1190" data-mp="1u25fs6ngoty6" />} onClick={() => setStagesOpen(true)} data-id="element-1189" data-mp="1u3q8dqq7u2te">
              Gestionar estados
            </Button>
          </>} data-id="element-1185" data-mp="dnb0z4gzroq2" />

      {/* Columnas ocultas */}
      {stages.filter(s => s.active).length > columns.length && <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface px-4 py-3" data-id="element-1191" data-mp="1yxkqipq6a9qd">
          <span className="text-xs font-medium text-muted-foreground" data-id="element-1192" data-mp="166swnywkxx67">
            Columnas ocultas:
          </span>
          {stages.filter(s => s.active && hidden.includes(s.id)).map(stage => <button key={stage.id} type="button" onClick={() => setHidden(prev => prev.filter(id => id !== stage.id))} className="inline-flex items-center gap-1.5 rounded-full bg-background px-2.5 py-1 text-xs font-medium text-foreground" data-id="element-1193" data-mp="1g3nwqm8i45ml">
                <EyeIcon className="h-3 w-3" aria-hidden="true" data-id="element-1194" data-mp="2z23lxf6294pr" />
                {stage.name}
              </button>)}
        </div>}

      <div className="-mx-4 overflow-x-auto px-4 pb-4 sm:mx-0 sm:px-0" data-id="element-1195" data-mp="2p5qr2dys4ktf">
        <div className="flex min-w-max gap-4" data-id="element-1196" data-mp="2u4bu0l5f1ilk">
          {columns.map(stage => {
          const stageLeads = leads.filter(l => l.stageId === stage.id);
          return <section key={stage.id} onDragOver={e => {
            if (!canEdit) return;
            e.preventDefault();
            setOverStage(stage.id);
          }} onDragLeave={() => setOverStage(null)} onDrop={() => drop(stage.id)} className={`w-[280px] flex-none rounded-2xl border bg-surface p-3 transition-colors ${overStage === stage.id ? 'border-primary bg-primary/5' : 'border-border'}`} data-id="element-1197" data-mp="3hvzcceom3lc8">
                <header className="mb-3 flex items-center justify-between gap-2 px-1" data-id="element-1198" data-mp="j90fj9c1ss7r">
                  <span className="flex min-w-0 items-center gap-2" data-id="element-1199" data-mp="eaqjyxmevplm">
                    <span className="h-2.5 w-2.5 flex-none rounded-full" style={{
                  backgroundColor: stage.color
                }} aria-hidden="true" data-id="element-1200" data-mp="a9db0eo0v6ah" />
                    <h2 className="truncate text-sm font-semibold text-foreground" data-id="element-1201" data-mp="bqr1drv8ojn">
                      {stage.name}
                    </h2>
                  </span>
                  <span className="flex flex-none items-center gap-1" data-id="element-1202" data-mp="3ro1xq5ledruu">
                    <span className="rounded-full bg-background px-2 py-0.5 text-xs font-medium text-muted-foreground" data-id="element-1203" data-mp="3mr1sfwwdgt4s">
                      {stageLeads.length}
                    </span>
                    <button type="button" aria-label={`Ocultar columna ${stage.name}`} onClick={() => setHidden(prev => [...prev, stage.id])} className="rounded-md p-1 text-muted-foreground hover:bg-background" data-id="element-1204" data-mp="3dpkfgne9h7kb">
                      <EyeOffIcon className="h-3.5 w-3.5" data-id="element-1205" data-mp="1ta5tggwsi260" />
                    </button>
                  </span>
                </header>

                <ul className="space-y-2" data-id="element-1206" data-mp="2299fntr96xoq">
                  {stageLeads.map(lead => {
                const member = members.find(m => m.userId === lead.assignedTo);
                const note = lastNote(lead.id);
                return <li key={lead.id} data-id="element-1207" data-mp="1afh432liu0by">
                        <article draggable={canEdit} onDragStart={() => setDragging(lead.id)} onDragEnd={() => setDragging(null)} className={`rounded-xl border border-border bg-background p-3 shadow-sm transition-opacity ${canEdit ? 'cursor-grab active:cursor-grabbing' : ''} ${dragging === lead.id ? 'opacity-50' : ''}`} data-id="element-1208" data-mp="1ein61y1lp5cm">
                          <Link to={`/app/leads/${lead.id}`} className="block text-sm font-semibold text-foreground hover:text-primary" data-id="element-1209" data-mp="3bdcu5pm6sa3h">
                            {fullName(lead.firstName, lead.lastName)}
                          </Link>
                          {shows('company') && <p className="mt-0.5 truncate text-xs text-muted-foreground" data-id="element-1210" data-mp="3ubp1r6xg9bwy">
                              {lead.company}
                            </p>}
                          {shows('phone') && <p className="mt-1.5 text-xs text-muted-foreground" data-id="element-1211" data-mp="7x5m76wqnqow">
                              {lead.phone}
                            </p>}
                          {shows('email') && <p className="truncate text-xs text-muted-foreground" data-id="element-1212" data-mp="hrxogo19n1q0">
                              {lead.email}
                            </p>}
                          {shows('service') && lead.service && <p className="mt-1 truncate text-xs text-foreground" data-id="element-1213" data-mp="rgl0ztuz0vfn">
                              {lead.service}
                            </p>}
                          {shows('budget') && lead.budget && <p className="text-xs font-medium text-foreground" data-id="element-1214" data-mp="hq05ifalg70y">
                              {lead.budget}
                            </p>}
                          {shows('note') && note && <p className={`mt-2 line-clamp-2 rounded-md border-l-2 px-2 py-1 text-[11px] leading-snug text-foreground ${noteColor(note.color).bg} ${noteColor(note.color).border}`} data-id="element-1215" data-mp="crynzystgfrl">
                              {note.body}
                            </p>}
                          {(cards.showSource || shows('date') || shows('owner') || shows('lastContact')) && <div className="mt-3 flex items-center justify-between gap-2" data-id="element-1216" data-mp="1zw9moy9x9thp">
                              {cards.showSource ? <SourceBadge source={lead.origin.source} sources={sources} data-id="element-1217" data-mp="2sikkk1659gat" /> : <span data-id="element-1218" data-mp="2ishr5zv420by" />}
                              <span className="flex items-center gap-2" data-id="element-1219" data-mp="2nrc8exdfjgty">
                                {shows('date') && <span className="text-[11px] text-muted-foreground" data-id="element-1220" data-mp="3bg3x3ert9h1z">
                                    {formatDate(lead.createdAt)}
                                  </span>}
                                {shows('lastContact') && lead.lastContactAt && <span className="text-[11px] text-muted-foreground" data-id="element-1221" data-mp="zu33e5d42eng">
                                    {formatDate(lead.lastContactAt)}
                                  </span>}
                                {shows('owner') && member && <Avatar name={fullName(member.firstName, member.lastName)} size="sm" data-id="element-1222" data-mp="lutyw2y9p8zb" />}
                              </span>
                            </div>}
                        </article>
                      </li>;
              })}
                  {stageLeads.length === 0 && <li className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground" data-id="element-1223" data-mp="21asr24rhoudx">
                      Sin leads en este estado
                    </li>}
                </ul>
              </section>;
        })}
        </div>
      </div>

      <Modal open={cardsOpen} onClose={() => setCardsOpen(false)} title="Personalizar tarjetas" footer={<Button onClick={() => setCardsOpen(false)} data-id="element-1225" data-mp="g7oksxrii8b5">Listo</Button>} data-id="element-1224" data-mp="17whxwmmmb5gi">
        <div className="space-y-5" data-id="element-1226" data-mp="g5bvfzjfcvo2">
          <div data-id="element-1227" data-mp="k8ia9vklrv65">
            <p className="mb-2 text-sm font-medium text-foreground" data-id="element-1228" data-mp="ka1qjm69e67x">Modo</p>
            <div className="grid grid-cols-2 gap-2" data-id="element-1229" data-mp="p8cw3kpitukc">
              {(['compacto', 'ampliado'] as const).map(mode => <button key={mode} type="button" onClick={() => setCards({
              mode
            })} className={`min-h-[44px] rounded-xl border px-4 text-sm font-medium capitalize transition-colors ${cards.mode === mode ? 'border-primary bg-primary/5 text-foreground' : 'border-border text-muted-foreground hover:border-primary/40'}`} data-id="element-1230" data-mp="tzuzsgixi990">
                  {mode}
                </button>)}
            </div>
            <p className="mt-2 text-xs text-muted-foreground" data-id="element-1231" data-mp="3c4w2qryzu1xe">
              En modo compacto solo se muestran nombre, empresa y teléfono.
            </p>
          </div>

          <Switch id="pipeline-source" label="Mostrar fuente en las tarjetas" checked={cards.showSource} onChange={showSource => setCards({
          showSource
        })} data-id="element-1232" data-mp="377ch36zyumvf" />

          <div data-id="element-1233" data-mp="2sgwubnrwj6v5">
            <p className="mb-2 text-sm font-medium text-foreground" data-id="element-1234" data-mp="2sh54v90jy036">
              Campos en modo ampliado
            </p>
            <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1235" data-mp="2odqfhrqq47d2">
              {CARD_FIELDS.map(field => <li key={field.key} data-id="element-1236" data-mp="2jh9perzyrixc">
                  <Checkbox id={`card-${field.key}`} label={field.label} disabled={compact} checked={cards.fields.includes(field.key)} onChange={e => setCards({
                fields: e.target.checked ? [...cards.fields, field.key] : cards.fields.filter(f => f !== field.key)
              })} data-id="element-1237" data-mp="2jib4r8o0irkv" />
                </li>)}
            </ul>
          </div>
        </div>
      </Modal>

      <StageManager open={stagesOpen} onClose={() => setStagesOpen(false)} organizationId={organizationId} stages={stages} data-id="element-1238" data-mp="3qu2d7zpvh0si" />
    </>;
}